// «Dar de alta desde el código» · la lógica sin React · U-03 fase a (DISEÑO §6.1)
//
// Vive aparte de la pantalla para que las reglas se prueben solas y para que
// la pantalla (un .jsx) solo exporte componentes.
//
// LA REGLA DE LA CASA (D18): la sugerencia del catálogo compartido solo llena
// un campo que la persona NO ha tocado. Si llega tarde, no pisa nada. El
// nombre que se guarda es el que está en el campo, y ningún proceso cambia
// un producto que ya existe.

// El `id` del módulo. Lo usan `carriles/gtin.modulos.jsx` (lo declara) y los
// dos botones de entrada, en Escanear y en Códigos por resolver (lo abren con
// `irA`). La prueba 20 comprueba las dos direcciones.
export const ID_ALTA = 'gtin-alta';

// Las mismas nueve del servidor (`schemas.UNIDADES`) y de `Productos.jsx`.
export const UNIDADES = ['PIEZA', 'KG', 'G', 'L', 'ML', 'M', 'CM', 'CAJA', 'PAQUETE'];
export const NOMBRE_UNIDAD = {
  PIEZA: 'Pieza', KG: 'Kilogramo', G: 'Gramo', L: 'Litro', ML: 'Mililitro',
  M: 'Metro', CM: 'Centímetro', CAJA: 'Caja', PAQUETE: 'Paquete',
};

// Los topes del servidor: `ProductoCrear` (sku 2–60, nombre 2–200) y
// `CodigoCrear` (código 4–120).
export const SKU_MIN = 2;
export const SKU_MAX = 60;
export const NOMBRE_MIN = 2;
export const NOMBRE_MAX = 200;
export const CODIGO_MIN = 4;
export const CODIGO_MAX = 120;

/** El código como se guarda: sin espacios a los lados. */
export function limpiarCodigo(c) {
  return String(c ?? '').trim();
}

/** El SKU se autocorrige mientras se escribe: sin espacios y en mayúsculas. */
export function limpiarSku(s) {
  return String(s ?? '').replace(/\s+/g, '').toUpperCase();
}

/** SKU por omisión: el mismo código en mayúsculas, si cabe en un SKU. */
export function skuPorOmision(codigo) {
  const s = limpiarSku(codigo);
  return s.length >= SKU_MIN && s.length <= SKU_MAX ? s : '';
}

export function problemaDelCodigo(c) {
  const v = limpiarCodigo(c);
  if (!v) return 'Escanea o escribe el código de barras del producto.';
  if (v.length < CODIGO_MIN) return `Un código lleva al menos ${CODIGO_MIN} caracteres. Revisa lo que escaneaste.`;
  if (v.length > CODIGO_MAX) return `Un código lleva hasta ${CODIGO_MAX} caracteres. Revisa lo que escaneaste.`;
  return null;
}

export function problemaDelNombre(n) {
  const v = String(n ?? '').trim();
  if (!v) return 'Escribe el nombre del producto.';
  if (v.length < NOMBRE_MIN) return `El nombre lleva al menos ${NOMBRE_MIN} letras.`;
  if (v.length > NOMBRE_MAX) return `El nombre lleva hasta ${NOMBRE_MAX} caracteres; quita ${v.length - NOMBRE_MAX}.`;
  return null;
}

export function problemaDelSku(s) {
  const v = limpiarSku(s);
  if (!v) return 'Escribe un SKU, o deja el mismo código.';
  if (v.length < SKU_MIN) return `El SKU lleva al menos ${SKU_MIN} caracteres.`;
  if (v.length > SKU_MAX) return `El SKU lleva hasta ${SKU_MAX} caracteres; quita ${v.length - SKU_MAX}.`;
  return null;
}

/**
 * Aplica la sugerencia SOLO a los campos que nadie ha tocado.
 *
 * `campos`  · { nombre, unidad }
 * `tocados` · { nombre: bool, unidad: bool }
 * Devuelve los campos nuevos. Un campo tocado se queda como está, aunque
 * esté vacío: borrar también es tocar.
 */
export function aplicarSugerencia(campos, tocados, sugerencia) {
  if (!sugerencia) return campos;
  const salida = { ...campos };
  const nombre = sugerencia.nombre_propuesto || sugerencia.nombre;
  if (!tocados.nombre && nombre) salida.nombre = String(nombre).slice(0, NOMBRE_MAX);
  if (!tocados.unidad && UNIDADES.includes(sugerencia.unidad_medida)) salida.unidad = sugerencia.unidad_medida;
  return salida;
}

/**
 * En qué estado está la sugerencia, a partir de la respuesta del servidor.
 *
 *   CARGANDO       · todavía no contesta
 *   ERROR          · la consulta falló (el formulario sigue usable)
 *   NO_COMPARTIBLE · el código no se busca en el catálogo compartido
 *   CON            · hay sugerencia
 *   SIN            · el código no está en el catálogo compartido
 *
 * Y aparte, `catalogo`: cómo está la lista de este servidor.
 *   SIN_COPIA · la lista no ha llegado nunca
 *   VIEJA     · la última actualización no terminó bien; se dice su fecha
 *   AL_DIA    · nada que decir
 */
export function estadoDeSugerencia(respuesta, { cargando = false, error = null } = {}) {
  if (cargando) return { estado: 'CARGANDO', catalogo: null };
  if (error) return { estado: 'ERROR', catalogo: null };
  const r = respuesta && typeof respuesta === 'object' ? respuesta : {};
  const cat = r.catalogo && typeof r.catalogo === 'object' ? r.catalogo : {};
  let catalogo = 'AL_DIA';
  if (!cat.actualizado_en) catalogo = 'SIN_COPIA';
  else if (cat.estado && cat.estado !== 'BIEN') catalogo = 'VIEJA';
  if (r.compartible === false) return { estado: 'NO_COMPARTIBLE', catalogo: null };
  if (r.sugerencia) return { estado: 'CON', catalogo };
  return { estado: 'SIN', catalogo };
}

/** La insignia de una sugerencia: verde si la revisó TEGO, amarilla si no. */
export function insigniaDe(sugerencia) {
  if (!sugerencia) return null;
  return sugerencia.fuente === 'CURADO'
    ? { color: 'verde', texto: 'Revisado por TEGO' }
    : { color: 'amarillo', texto: 'Sugerencia de otra tienda · revísala' };
}

/** Cómo llegó el código, en el vocabulario de `CodigoCrear.origen`. */
export function origenDelCodigo(medio) {
  return medio === 'TECLADO' || medio === 'TECLEADO' ? 'TECLEADO' : 'ESCANEADO';
}
